// ASTM E3106 risk-score scales rendered on the Dashboard.
//
// Keys mirror RiskScoreScales.cs (toxicity / cpu / di / vdi) — keep both in sync.
// Everything here is presentation: the axis bounds, the colour ramp and the caption
// text. The scores themselves are derived server-side from existing master data and
// assessment input; nothing on these scales is separately captured.
//
// `gradient` stops are given in score space (not percentages) so a stop stays put when
// the axis changes. The colours are deliberately fixed rather than theme-derived — the
// green→red ramp is the meaning of the scale, so it must read identically in light and
// dark mode.

export const RISK_SCORE_KEYS = {
  TOXICITY: 'toxicity',
  CPU: 'cpu',
  DI: 'di',
  VDI: 'vdi',
};

// Shared ramp for the 0-10 hazard scales (0 = safest, 10 = worst).
const HAZARD_RAMP = [
  { at: 0, color: '#0b6b00' },
  { at: 2, color: '#3f9c00' },
  { at: 4, color: '#b8d600' },
  { at: 5, color: '#f2e800' },
  { at: 7, color: '#ff8a00' },
  { at: 10, color: '#f01d00' },
];

// Detectability ramp: green (detectable) below the failure threshold, a hard step to
// solid red at and above it. `redFrom` is where the method stops being fit for purpose.
const detectabilityRamp = (redFrom) => [
  { at: -5, color: '#0b6b00' },
  { at: -5 + (redFrom + 5) * 0.55, color: '#8fbe00' },
  { at: redFrom - 0.35, color: '#f2e800' },
  { at: redFrom, color: '#f01d00' },
  { at: 5, color: '#f01d00' },
];

export const RISK_SCORE_SCALES = [
  {
    key: RISK_SCORE_KEYS.TOXICITY,
    title: 'Toxicity Score',
    basis: 'HBEL (ADE/PDE) derived scale for toxicological evaluation of product in a shared facility.',
    formula: 'Toxicity Score = log(HBEL grams/day)',
    standard: 'ASTM E3106',
    source: 'Product APIs and cleaning agents',
    min: 0,
    max: 10,
    tickStep: 1,
    gradient: HAZARD_RAMP,
  },
  {
    key: RISK_SCORE_KEYS.CPU,
    title: 'Cpu Score',
    basis: 'Process capability scale for assessing the risk of compound carryover in a shared facility.',
    formula: 'Process Capability (Cpu) Score = (1/Cpu) × 10',
    standard: 'ASTM E3106',
    source: 'FMECA process steps',
    min: 0,
    max: 10,
    tickStep: 1,
    gradient: HAZARD_RAMP,
  },
  {
    key: RISK_SCORE_KEYS.DI,
    title: 'DI Score',
    basis: 'Swab-limit derived scale for assessing analytical detectability.',
    formula: 'Detection Index (DI) = log(Detection Limit (ppm) / Swab Limit (ppm))',
    standard: 'ASTM E3106',
    source: 'FMECA process steps',
    min: -5,
    max: 5,
    tickStep: 1,
    redFrom: 0,
    gradient: detectabilityRamp(0),
  },
  {
    key: RISK_SCORE_KEYS.VDI,
    title: 'VDI Score',
    basis: 'MSSR derived scale for assessing the detectability of visual inspection.',
    formula: 'Visual Detection Index (VDI) = log(Visual Residue Limit / Maximum Safe Surface Residue)',
    standard: 'ASTM E3106 and E3263',
    source: 'New Product Assessments with a determined VRL',
    min: -5,
    max: 5,
    redFrom: -1,
    tickStep: 1,
    gradient: detectabilityRamp(-1),
  },
];

// Build the CSS ramp for a scale — stops converted from score space to percentages,
// bottom (min) to top (max).
export function gradientCss({ gradient, min, max }) {
  const span = max - min || 1;
  const stops = gradient
    .map(({ at, color }) => `${color} ${(((at - min) / span) * 100).toFixed(2)}%`)
    .join(', ');
  return `linear-gradient(to top, ${stops})`;
}
